import { APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { NgModule } from '@angular/core';
import { ApolloLink, DefaultOptions, InMemoryCache } from '@apollo/client/core';
import { environment } from 'src/environments/environment';
import { setContext } from '@apollo/client/link/context';
import { AuthService } from 'src/app/auth/auth.service';
import { onError } from "@apollo/client/link/error";
import { UserStateService } from 'src/app/shared/services/user-state.class';
import { isAllErrorsWillBeProcessedInApp, isEnumValidationError, isJavaScriptRuntimeError } from './utils/error-processor';
import { firstValueFrom, lastValueFrom, distinctUntilChanged, shareReplay, filter, take } from 'rxjs';
import { CoralogixLogSeverity, CoralogixRum } from '@coralogix/browser';

const uri = environment.graphQlURI;

// Cache for the IoT token to reduce repeated requests
let cachedTokenPromise: Promise<string | undefined> | null = null;
let tokenExpiryTime: number | null = null;
let timeoutLogged = false; // Track if we've already logged the timeout message
let cognitoWarningLogged = false; // Track if we've already logged the no-token warning

export function createApollo(httpLink: HttpLink, authService: AuthService, userStateService: UserStateService) {
  const basic = setContext((operation, context) => ({
    headers: {
      Accept: 'application/json',
    },
  }));

  // Function to get Cognito token asynchronously
  const getCognitoToken = (): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!authService.cognitoUser) {
        // Expected during app startup - don't log warning
        resolve(null);
        return;
      }

      authService.cognitoUser.getSession((err: any, session: any) => {
        if (err) {
          // Only log session errors at debug level during startup
          console.debug('Cognito session not ready yet:', err?.message || 'Session pending');
          resolve(null);
        } else if (session && session.accessToken) {
          resolve(session.accessToken.jwtToken);
        } else {
          resolve(null);
        }
      });
    });
  };

  const auth = setContext(async (operation, context) => {
    try {
      // console.log(`[GraphQL] Operation: ${operation.operationName}`, {
      //   query: operation.query.loc?.source.body.substring(0, 100) + '...'
      // });

      // Get Cognito token asynchronously
      const cognitoToken = await getCognitoToken();

      if (!cognitoToken) {
        // Only warn once per session to reduce noise during app launch
        if (!cognitoWarningLogged) {
          console.debug('No Cognito token available for GraphQL request');
          cognitoWarningLogged = true;
        }
        return {};
      }
      // Reset warning flag when token becomes available
      cognitoWarningLogged = false;
      
      // Check if user is fully authenticated before trying to get IoT token
      const isFullyAuthenticated = authService.isFullyAuthenticated();
      if (!isFullyAuthenticated) {
        // User not fully authenticated yet (e.g., at PIN screen)
        return {
          headers: {
            Authorization: cognitoToken,
          },
        };
      }
      
      try {
        // Get the IoT token, using the cached promise if available and not expired
        const now = Date.now();
        if (!cachedTokenPromise || !tokenExpiryTime || now >= tokenExpiryTime) {
          // Create a new promise and cache it
          // Set expiry to 5 minutes to avoid too many refreshes
          tokenExpiryTime = now + (5 * 60 * 1000);
          
          // First ensure token loading is complete
          await authService.ensureTokenLoaded();
          
          // Check if token already exists
          const currentToken = await firstValueFrom(authService.getIotToken().pipe(take(1)));
          if (currentToken) {
            // Token already available, use it immediately
            cachedTokenPromise = Promise.resolve(currentToken);
          } else {
            // No token yet - wait for token to become available if user is fully authenticated
            // Use a longer timeout to give the token request time to complete
            cachedTokenPromise = Promise.race([
              // Wait for token to become available (filter out undefined values)
              firstValueFrom(
                authService.getIotToken().pipe(
                  filter((token): token is string => token !== undefined), // Wait for actual token
                  take(1)
                )
              ).catch(() => undefined), // Return undefined if error
              // Longer timeout (5 seconds) to allow token request to complete
              new Promise<string | undefined>((resolve) => 
                setTimeout(() => {
                  // Only log timeout once per session to reduce noise during app launch
                  if (!timeoutLogged) {
                    console.debug('IoT token not yet available for GraphQL - proceeding without it');
                    timeoutLogged = true;
                  }
                  resolve(undefined);
                }, 5000) // 5 second timeout to allow token request to complete
              )
            ]);
          }
        }
        
        const iotToken = await cachedTokenPromise;
        
        // Always include the Authorization header
        const headers: any = {
          Authorization: cognitoToken,
        };
        
        // Include IoT token if available
        if (iotToken) {
          headers['Iot-Auth-Token'] = iotToken;
          // Reset timeout logged flag when token becomes available
          timeoutLogged = false;
          // Log successful token inclusion only in debug
          // console.log('IoT token included in GraphQL request');
        } else {
          // Only warn if token is still not available after checking
          // Check if token is available now (might have loaded after timeout)
          const currentToken = await firstValueFrom(authService.getIotToken().pipe(take(1)));
          if (currentToken) {
            // Token became available, use it and clear cache for next request
            headers['Iot-Auth-Token'] = currentToken;
            cachedTokenPromise = null;
            tokenExpiryTime = null;
            timeoutLogged = false; // Reset flag since token is now available
          } else {
            // Token still not available - only warn if this persists beyond initialization
            // (Don't warn during normal app launch when token is still loading)
            console.debug('IoT token not yet available for GraphQL request');
          }
        }
        
        return { headers };
      } catch (error) {
        console.error('Error setting up GraphQL headers:', error);
        // Return at least the Authorization header if we have it
        return {
          headers: {
            Authorization: cognitoToken,
          },
        };
      }
    } catch (error) {
      console.error('Error in GraphQL auth setup:', error);
      return {};
    }
  });

  // Add a link to log successful responses
  const responseLoggerLink = new ApolloLink((operation, forward) => {
    return forward(operation).map(response => {
      // console.log(`[GraphQL] Success: ${operation.operationName}`, {
      //   data: response.data
      // });
      return response;
    });
  });

  // Track if we've logged the auth error to avoid spam
  let authErrorLogged = false;

  const errorLink = onError(({ graphQLErrors, networkError, operation, forward, response }) => {
    if (graphQLErrors) {
      // handle graphQL error
      // console.log(`[GraphQL] ERROR in ${operation.operationName}:`, graphQLErrors);

      // Check for authentication errors - these are expected during startup
      const isAuthError = graphQLErrors.some(error => {
        const message = error.message || '';
        return message.includes('Access denied') ||
               message.includes('authenticated') ||
               message.includes('Unauthorized');
      });

      if (isAuthError) {
        // Auth errors during startup are expected - log once and don't mark Apollo unavailable
        if (!authErrorLogged) {
          console.debug('GraphQL auth error during startup - requests will retry after authentication');
          authErrorLogged = true;
        }
        // Keep Apollo available - requests will work once authenticated
        if (!userStateService.isApolloAvailable.value) {
          userStateService.isApolloAvailable.next(true);
        }
        return forward(operation);
      }

      // Reset auth error flag when we get non-auth errors (auth is working)
      authErrorLogged = false;

      if (environment.environmentName === 'production') {
        CoralogixRum.log(CoralogixLogSeverity.Error, 'GraphQL Errors', graphQLErrors);
      }

      // Special handling for enum validation errors - never mark Apollo as unavailable for these
      const allErrorsAreEnumValidation = graphQLErrors.every(error => isEnumValidationError(error));
      if (allErrorsAreEnumValidation) {
        // console.log('All errors are enum validation errors, keeping Apollo available');
        // Keep Apollo available for these errors
        if (!userStateService.isApolloAvailable.value) {
          userStateService.isApolloAvailable.next(true);
        }
        return forward(operation);
      }

      const willProcessedInApp = isAllErrorsWillBeProcessedInApp(graphQLErrors);
      if (!willProcessedInApp) {
        console.log('Setting Apollo to unavailable due to unhandled errors');
        userStateService.isApolloAvailable.next(false);
      } else {
        // Make sure Apollo stays available for errors we can handle
        if (!userStateService.isApolloAvailable.value) {
          userStateService.isApolloAvailable.next(true);
        }
      }
    }

    // Handle JavaScript runtime errors that might occur during Apollo operations
    if (networkError && isJavaScriptRuntimeError(networkError)) {
      console.warn('Ignoring non-critical JavaScript runtime error:', networkError.message);
      // Don't set isApolloAvailable to false for these errors
      return forward(operation);
    }

    if (networkError) {
      console.log('Network Error:', networkError);
      
      // Check if this is an IoT token related error
      const errorMessage = networkError.message || '';
      const isIotTokenError = errorMessage.includes('Iot-Auth-Token') || 
                             (networkError as any).status === 500;
      
      if (isIotTokenError) {
        console.warn('GraphQL request failed due to missing IoT token - app will continue with MQTT');
        // Don't mark Apollo as unavailable for IoT token issues
        // The app can function with MQTT alone
      } else {
        userStateService.isApolloAvailable.next(false);
      }
      if (environment.environmentName === 'production') {
        CoralogixRum.log(CoralogixLogSeverity.Error, 'Network Errors', networkError);
      }
    }

    return forward(operation);
  });

  const apolloAvailableLink = new ApolloLink((operation, forward) => {
    if (userStateService.isApolloAvailable.value != true) {
      userStateService.isApolloAvailable.next(true);
    }
    return forward(operation);
  });

  const link = ApolloLink.from([apolloAvailableLink, responseLoggerLink, errorLink, basic, auth, httpLink.create({ uri })]);
  const cache = new InMemoryCache();
  const defaultOptions: DefaultOptions = {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'ignore',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    },
  }

  return {
    link,
    cache,
    defaultOptions,
  };
}

@NgModule({
  exports: [ApolloModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, AuthService, UserStateService],
    },
  ],
})
export class GraphQLModule {
  constructor(authService: AuthService, public userStateService: UserStateService) {
    // console.log('[GraphQL] Module initialized', {
    //   httpEndpoint: uri,
    //   wsEndpoint: wsUri,
    //   environment: environment.environmentName
    // });

    // Subscribe to Apollo availability changes
    // userStateService.isApolloAvailable.subscribe(available => {
    //   console.log(`[GraphQL] Apollo availability changed: ${available ? 'Available' : 'Unavailable'}`);
    // });
  }
}
